import { useRegisterSW } from 'virtual:pwa-register/react'
import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import buildInfo from '../../public/version.json'

// Version avec laquelle CE bundle a été compilé (figée au build). On la compare à la
// version LIVE servie par /version.json : si elles diffèrent, ce client tourne une version
// périmée → on propose de rafraîchir. (Avant, on comparait la version serveur à elle-même
// → un client déjà en retard à l'ouverture n'était jamais détecté.)
const BUILD_VERSION = (buildInfo as { version: string }).version

export default function UpdateBanner() {
  const [dismissed, setDismissed] = useState(false)
  const [versionMismatch, setVersionMismatch] = useState(false)
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, r) {
      if (r) {
        r.update()
        setInterval(() => {
          r.update()
        }, 60 * 1000)
      }
    },
  })

  // Fallback poll version.json : détecte une version périmée même sans SW waiting (iOS PWA
  // notamment). On compare la version LIVE à BUILD_VERSION (celle de ce bundle), pas à la
  // 1re valeur lue → un client déjà en retard à l'ouverture est détecté immédiatement.
  useEffect(() => {
    let cancelled = false
    const check = async () => {
      try {
        const res = await fetch('/version.json?_=' + Date.now(), { cache: 'no-store' })
        const data = await res.json()
        if (cancelled) return
        if (data.version && data.version !== BUILD_VERSION) setVersionMismatch(true)
      } catch {}
    }
    check()
    const id = setInterval(check, 60 * 1000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  const visible = (needRefresh || versionMismatch) && !dismissed
  if (!visible) return null

  const handleRefresh = async () => {
    try {
      // 1. skipWaiting + activate new SW si present
      await updateServiceWorker(false)
    } catch {}
    try {
      // 2. purge caches (au cas ou le SW refuse de lacher)
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    } catch {}
    // 3. hard reload (casse le cache HTTP aussi)
    window.location.reload()
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-terre to-soleil flex items-center justify-center gap-3 sm:gap-4 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] shadow-lg"
      role="status"
      aria-live="polite"
    >
      <div className="min-w-0">
        <p className="text-creme text-sm font-medium">Nouvelle version disponible</p>
        <p className="text-xs text-creme/80 mt-0.5 hidden sm:block">
          Rafraîchis pour profiter des dernières améliorations.
        </p>
      </div>
      <button
        onClick={handleRefresh}
        className="px-3 sm:px-4 py-2 bg-creme text-terre text-sm font-semibold rounded-lg hover:bg-white transition shrink-0"
      >
        Rafraîchir
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="p-1 text-creme/70 hover:text-creme transition shrink-0"
        aria-label="Plus tard"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
